import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";

import { downloadGeneratedDocument } from "../../../../route/developer/activities";
import { SearchActivityHistoryResponse } from "../../../../service/query";
import { searchActivityDefinitionDocumentTypes } from "../../../../service/query/QueryService.full";
import { ResultData, Status } from "../../../../service/Shared";
import { flattenObject } from "../../../../utils";
import { DataGridColumnDefinition } from "../../../../widget";

interface UseVersionConflictModalReturnData {
  navigate: NavigateFunction;
  tableColumnsTopProjects: DataGridColumnDefinition[];
  data?: ResultData[];
  canOutputActivity: boolean;
  downloadDraft: () => Promise<void>;
  isDocumentDownloading: boolean;
  isResumeLatestActivityLoading: boolean;
  setIsResumeLatestActivityLoading: Dispatch<SetStateAction<boolean>>;
}

export const useVersionConflictModal = (
  draftActivityHistoryUuid?: string,
  newVersionActivityHistory?: SearchActivityHistoryResponse
): UseVersionConflictModalReturnData => {
  const navigate = useNavigate();

  const [canOutputActivity, setCanOutputActivity] = useState(false);
  const [isDocumentDownloading, setIsDocumentDownloading] = useState(false);
  const [isResumeLatestActivityLoading, setIsResumeLatestActivityLoading] = useState(false);

  const activityDefinitionUuid = newVersionActivityHistory?.results[0]?.activity?.activityDefinition?.uuid;

  useEffect(() => {
    if (!activityDefinitionUuid) return;

    searchActivityDefinitionDocumentTypes({
      paging: { limit: 1, beforeCursor: null, afterCursor: null },
      filter: {
        results: {
          activityDefinitionUuid: { operator: "eq", value: activityDefinitionUuid },
          activityDefinitionVersionDocumentTypes: {
            some: { templateFileUuid: { operator: "neq", value: null } },
          },
        },
      },
    }).then((response) => {
      if (response.status === Status.Success && response.data) {
        setCanOutputActivity(response.data.results.length > 0);
      }
    });
  }, [activityDefinitionUuid]);

  const downloadDraft = async (): Promise<void> => {
    if (draftActivityHistoryUuid) {
      setIsDocumentDownloading(true);
      await downloadGeneratedDocument({ activityHistoryUuid: draftActivityHistoryUuid });

      setIsDocumentDownloading(false);
    }
  };

  const data = newVersionActivityHistory?.results?.map((el) => flattenObject(el));

  const tableColumnsTopProjects: DataGridColumnDefinition[] = [
    {
      key: "activity.activityDefinition.displayName",
      name: "Activity",
      dataType: "string",
      alignment: "left",
      formatter: "align",
    },
    {
      key: "versionNumber",
      name: "Version",
      dataType: "string",
      alignment: "left",
      formatter: "align",
    },
    {
      key: "createdByUser.fullName",
      name: "Created by",
      dataType: "string",
      alignment: "left",
      formatter: "align",
    },
    {
      key: "createdAt",
      name: "Date created",
      dataType: "Date",
      formatter: "dateOnly",
      alignment: "center",
      minWidth: 150,
      filterable: false,
    },
  ];

  return {
    navigate,
    tableColumnsTopProjects,
    data,
    canOutputActivity,
    downloadDraft,
    isDocumentDownloading,
    isResumeLatestActivityLoading,
    setIsResumeLatestActivityLoading,
  };
};
